import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Pagination,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getSalesImports } from "../api/salesImportsApi";
import SalesImportCardShell from "../components/SalesImportCardShell";
import SalesImportFilters from "../components/SalesImportFilters";
import SalesImportsTable from "../components/SalesImportsTable";
import { getSalesImportErrorMessage } from "../utils/salesImportError";
import { normalizeSalesImportList } from "../utils/salesImportListModel";

const PAGE_SIZE = 20;
const LIST_STATE_KEY = "salesImportListState";
const INTEGRATION_SUCCESS_MESSAGE = "Las ventas fueron integradas correctamente.";

function unwrapListResponse(response) {
  if (Array.isArray(response?.data) && response?.meta) return response;
  if (response?.data && typeof response.data === "object") return response.data;
  return response;
}

function positivePage(value) {
  const page = Number(value);
  return Number.isSafeInteger(page) && page > 0 ? page : 1;
}

export default function VentasImportadasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const initialListState = useRef(location.state?.[LIST_STATE_KEY] || {}).current;
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: String(initialListState.filters?.dateFrom || ""),
    dateTo: String(initialListState.filters?.dateTo || ""),
  });
  const [page, setPage] = useState(positivePage(initialListState.page));
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage ||
      (location.state?.salesImportIntegrated
        ? INTEGRATION_SUCCESS_MESSAGE
        : "")
  );
  const [recentSalesImportId] = useState(() => {
    const id = Number(location.state?.recentSalesImportId);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  });

  const invalidDateRange = Boolean(
    filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo
  );
  const hasFilters = Boolean(filters.dateFrom || filters.dateTo);

  const loadSalesImports = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      if (invalidDateRange) {
        setRows([]);
        setTotalItems(0);
        setTotalPages(0);
        setLoading(false);
        setError("La fecha desde no puede ser posterior a la fecha hasta.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const response = await getSalesImports({
          page,
          limit: PAGE_SIZE,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          signal,
        });
        if (signal.aborted || requestId !== latestRequestId.current) return;

        const result = normalizeSalesImportList(unwrapListResponse(response));
        const lastAvailablePage = Math.max(result.meta.totalPages, 1);
        if (page > lastAvailablePage) {
          setPage(lastAvailablePage);
          return;
        }
        setRows(result.data);
        setTotalItems(result.meta.totalItems);
        setTotalPages(result.meta.totalPages);
      } catch (requestError) {
        if (signal.aborted || requestId !== latestRequestId.current) return;
        setRows([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(
          getSalesImportErrorMessage(
            requestError,
            "No se pudieron cargar las ventas importadas."
          )
        );
      } finally {
        if (!signal.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [filters.dateFrom, filters.dateTo, invalidDateRange, page]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadSalesImports(controller.signal);
    return () => controller.abort();
  }, [loadSalesImports, reloadVersion]);

  useEffect(() => {
    if (!location.state?.successMessage && !location.state?.salesImportIntegrated) {
      return;
    }
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: initialListState && Object.keys(initialListState).length
        ? { [LIST_STATE_KEY]: initialListState }
        : null,
    });
  }, [initialListState, location.pathname, location.search, location.state, navigate]);

  const listState = useMemo(
    () => ({ filters: { ...filters }, page }),
    [filters, page]
  );
  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin importaciones para mostrar";
    return `Mostrando ${rows.length} de ${totalItems} importaciones`;
  }, [rows.length, totalItems]);

  function handleFilterChange(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  }

  function handleClearFilters() {
    setFilters({ dateFrom: "", dateTo: "" });
    setPage(1);
  }

  function showRecentImport() {
    handleClearFilters();
  }

  function handleImport() {
    navigate("/stock/operacion/ventas-importadas/importar", {
      state: { [LIST_STATE_KEY]: listState },
    });
  }

  function handleView(salesImport) {
    navigate(`/stock/operacion/ventas-importadas/${salesImport.id}`, {
      state: { [LIST_STATE_KEY]: listState },
    });
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Ventas importadas"
        onBack={() => navigate("/stock/operacion")}
      />

      <SalesImportCardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Stack spacing={0.4}>
              <SalesImportFilters
                {...filters}
                onChange={handleFilterChange}
                onClear={handleClearFilters}
              />
            </Stack>

            <Button
              type="button"
              onClick={handleImport}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-end", md: "center" },
                minWidth: 150,
                height: 38,
                px: 2.4,
                borderRadius: "33px",
                bgcolor: "primary.500",
                color: "logoblanco",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 800,
                boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                "&:hover": { bgcolor: "primary.600" },
              }}
            >
              Importar ventas
            </Button>
          </Stack>

          {error ? (
            <Box
              role="alert"
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}
                  >
                    No se pudo completar la operación
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "logoblanco",
                      opacity: 0.95,
                    }}
                  >
                    {error}
                  </Typography>
                </Box>
                {!invalidDateRange ? (
                  <Button
                    size="small"
                    onClick={() => setReloadVersion((current) => current + 1)}
                    sx={{
                      color: "logoblanco",
                      textTransform: "none",
                      fontWeight: 800,
                    }}
                  >
                    Reintentar
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : null}

          {!error ? (
            <>
              <SalesImportsTable
                rows={rows}
                loading={loading}
                onView={handleView}
                emptyMessage={
                  hasFilters
                    ? "No se encontraron importaciones para el período seleccionado."
                    : "Todavía no hay ventas importadas."
                }
              />

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                sx={{
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  pt: 0.5,
                }}
              >
                <Typography
                  sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}
                >
                  {pageSummary}
                </Typography>
                <Pagination
                  page={page}
                  count={Math.max(totalPages, 1)}
                  onChange={(_, value) => setPage(value)}
                  shape="rounded"
                  color="primary"
                  disabled={loading || totalPages <= 1}
                />
              </Stack>
            </>
          ) : null}
        </Stack>
      </SalesImportCardShell>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={recentSalesImportId && (hasFilters || page > 1) ? 8000 : 3500}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSuccessMessage("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage("")}
          sx={{ width: "100%" }}
        >
          {successMessage}
          {recentSalesImportId && (hasFilters || page > 1) ? (
            <Button color="inherit" size="small" onClick={showRecentImport} sx={{ ml: 1, whiteSpace: "nowrap" }}>
              Ver nueva importación
            </Button>
          ) : null}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
